import dayjs from "dayjs";
import styled from "styled-components";

import { AppNav } from "@components/Header/AppNav";
import { FutureMoneyLogo } from "@components/Header/FutureMoneyLogo";
import { useTheme, useUser } from "@hooks";
import { Translations, i18n } from "@i18n";
import { AccountStatusSection } from "@routes/DashboardRoute/AccountStatusSection";
import { BeneficiarySsnBanner } from "@routes/DashboardRoute/BeneficiarySsnBanner";
import { BeneficiariesSection } from "./BeneficiariesSection";
import { DoMoreSection } from "./DoMoreSection";
import { MoneyRail } from "./MoneyRail";
import { PartnerBanner } from "./PartnerBanner";
import { PartnershipFooter } from "./PartnershipFooter";
import { ProjectionCard } from "./ProjectionCard";
import { Content, Eyebrow, Grain, Page, SerifTitle } from "./styles";
import { home, homeCssVars } from "./tokens";

const Hero = styled.div`
  padding: 28px ${home.pagePad} 30px;
  display: flex;
  flex-direction: column;
  gap: 34px;
`;

const Greeting = styled(SerifTitle)`
  font-size: 46px;
  line-height: 1.06;
  letter-spacing: -0.5px;

  @media (max-width: 720px) {
    font-size: 34px;
  }
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 396px;
  gap: 20px;
  align-items: stretch;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Footer = styled.div`
  padding: 56px ${home.pagePad} 48px;

  > div {
    padding-top: 26px;
    border-top: 1px solid ${home.line08};
    display: flex;
    align-items: flex-start;
    gap: 40px;
  }
`;

const Disclaimer = styled.div`
  font-size: 12px;
  line-height: 1.65;
  color: ${home.ink34};
  max-width: 760px;
`;

const FooterMark = styled.div`
  opacity: 0.5;
  margin-left: auto;
  transform: scale(0.78);
  transform-origin: right top;

  svg {
    display: block;
  }
`;

const Banners = styled.div`
  padding: 24px ${home.pagePad} 0;
  display: flex;
  flex-direction: column;

  &:empty {
    display: none;
  }
`;

export const Home = () => {
  const { user } = useUser();
  const { theme } = useTheme();

  const name = user?.preferredName || user?.firstName || "";

  return (
    <Page style={homeCssVars(theme.meta.dark)}>
      <Grain aria-hidden="true" />
      <Content>
        <AppNav />

        <Banners>
          <AccountStatusSection />
          <BeneficiarySsnBanner />
        </Banners>

        <Hero>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Eyebrow>
              {i18n.t(Translations.HomeEyebrowHousehold)} &middot; {dayjs().format("MMMM YYYY")}
            </Eyebrow>
            <Greeting as="h1" style={{ margin: 0 }}>
              {i18n.t(Translations.HomeGreetingLead)}{" "}
              <em>{i18n.t(Translations.HomeGreetingHighlight)}</em>
              {name ? `, ${name}.` : "."}
            </Greeting>
          </div>

          <HeroGrid>
            <ProjectionCard />
            <MoneyRail />
          </HeroGrid>
        </Hero>

        <Banners>
          <PartnerBanner />
        </Banners>

        <BeneficiariesSection />
        <DoMoreSection />

        <Footer>
          <div>
            <Disclaimer>
              <PartnershipFooter />
              {i18n.t(Translations.HomeDisclaimer)}
            </Disclaimer>
            <FooterMark>
              <FutureMoneyLogo />
            </FooterMark>
          </div>
        </Footer>
      </Content>
    </Page>
  );
};
